import { Site } from '@envato/foundation-types'
import isEmpty from 'lodash-es/isEmpty'
import get from 'lodash-es/get'

import { IExperimentsConfig } from '../../config/experiments'
import { getConfigBySite } from '../../config/sites'
import { ICommonLocation } from './location/types'
import { getExperimentCookie } from './cookieHelper'
import { Consent } from './consent'

export const isExperimentRunning = ({
  experimentsConfig,
  experimentName,
  currentSite,
  location
}: {
  experimentsConfig: IExperimentsConfig
  experimentName: string
  currentSite: Site
  location: ICommonLocation
}) => {
  const experiment = experimentsConfig[experimentName]

  if (!experiment) {
    return false
  }

  const experimentsQuery = get(location, 'query.experiments', '').split(',')
  const isRunning =
    experiment.running || experimentsQuery.includes(experimentName)
  const isForCurrentSite =
    isEmpty(experiment.sites) || experiment.sites.includes(currentSite)

  const isRunningForCurrentSite = isRunning && isForCurrentSite

  return isRunningForCurrentSite
}

export const isExperimentIndependent = (
  experimentsConfig: IExperimentsConfig,
  experimentName: string
) => !experimentsConfig[experimentName].requires

export const hasFulfilledExperimentDependency = (
  experimentsConfig: IExperimentsConfig,
  experimentName: string
) => {
  const requiresConfig = experimentsConfig[experimentName].requires
  if (!requiresConfig) {
    return true
  }

  return Object.entries(requiresConfig)
    .map(([dependentExperimentName, { variants }]) => {
      const dependentExperimentConfig =
        experimentsConfig[dependentExperimentName]
      if (!dependentExperimentConfig) {
        throw new Error(
          `Dependent "${dependentExperimentName}" experiment does not exist`
        )
      }
      const dependentExperimentId = dependentExperimentConfig.experimentId
      const dependentExperimentVariant = getExperimentCookie({
        experimentId: dependentExperimentId,
        consentType: Consent.preferences
      })

      return variants.includes(Number(dependentExperimentVariant))
    })
    .every(value => !!value)
}

export const shouldDisplayLoadingCover = ({
  experimentsReady,
  experimentsConfig,
  currentSite
}: {
  experimentsReady: boolean
  experimentsConfig: IExperimentsConfig
  currentSite: Site
}) => {
  const isCoverEnabledBySiteConfig = getConfigBySite(
    currentSite,
    'experimentsLoadingCover'
  )
  const isCoverEnabledByExperimentsConfig = Object.keys(experimentsConfig)
    .map(key => experimentsConfig[key])
    .some(expConfig => {
      const isForCurrentSite =
        isEmpty(expConfig.sites) || expConfig.sites.includes(currentSite)
      return isForCurrentSite && !!expConfig.loadingCover
    })

  return (
    (isCoverEnabledByExperimentsConfig || isCoverEnabledBySiteConfig) &&
    !experimentsReady
  )
}
